/**
 * Payment Reconciliation — API client.
 *
 * Thin typed wrapper over fetchApi (which unwraps the {success,data} envelope).
 */

import { fetchApi } from './api';
import type {
    ReconInvoice, ReconTransaction, ReconImport, ReconCategory, ReconBankAccount,
    ReconTemplate, ReconAlias, ReconStats, ColumnMapping, AllocationInput, MatchSuggestion,
    PaymentReconComment,
} from '../types/paymentRecon';

const DEFAULT_COMPANY = 'dfr';

function qs(params: Record<string, string | number | undefined>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
}

export const paymentReconApi = {
    // ---- Companies ----------------------------------------------------------
    listCompanies() {
        return fetchApi<{ companies: { code: string; name: string }[] }>(`/payment-recon/companies`);
    },

    // ---- Invoices / OP list -------------------------------------------------
    /** `q` = one substring; `qAny` = space-separated tokens, ANY of which may match. */
    listInvoices(opts: { companyCode?: string; status?: string; q?: string; qAny?: string; customerCode?: string; limit?: number } = {}) {
        return fetchApi<{ companyCode: string; invoices: ReconInvoice[] }>(
            `/payment-recon/invoices${qs({ companyCode: opts.companyCode ?? DEFAULT_COMPANY, status: opts.status, q: opts.q, qAny: opts.qAny, customerCode: opts.customerCode, limit: opts.limit })}`
        );
    },
    syncInvoices(companyCode = DEFAULT_COMPANY) {
        return fetchApi<{ companyCode: string; inserted: number; updated: number }>(
            `/payment-recon/invoices/sync`,
            { method: 'POST', body: JSON.stringify({ companyCode }) }
        );
    },
    markInvoicesPaid(invoiceIds: number[], paid = true, paidDate?: string | null, note?: string) {
        return fetchApi<{ updated: number; paid: boolean }>(
            `/payment-recon/invoices/mark-paid`,
            { method: 'POST', body: JSON.stringify({ invoiceIds, paid, paidDate, note }) }
        );
    },

    // ---- Transactions -------------------------------------------------------
    listTransactions(opts: { companyCode?: string; status?: string; importId?: number; q?: string; limit?: number } = {}) {
        return fetchApi<{ companyCode: string; transactions: ReconTransaction[] }>(
            `/payment-recon/transactions${qs({ companyCode: opts.companyCode ?? DEFAULT_COMPANY, status: opts.status, importId: opts.importId, q: opts.q, limit: opts.limit })}`
        );
    },
    match(body: {
        transactionId: number; allocations: AllocationInput[];
        onAccountRemainder?: boolean; learnAlias?: boolean; customerCode?: string | null; customerName?: string | null;
    }) {
        return fetchApi<{ transactionId: number; matchedAmount: number; status: string }>(
            `/payment-recon/match`, { method: 'POST', body: JSON.stringify(body) }
        );
    },
    setNonCustomer(transactionId: number, categoryId: number, note?: string) {
        return fetchApi<{ transactionId: number; status: string }>(
            `/payment-recon/transactions/${transactionId}/non-customer`,
            { method: 'POST', body: JSON.stringify({ categoryId, note }) }
        );
    },
    setOnAccount(transactionId: number, note?: string) {
        return fetchApi<{ transactionId: number; status: string }>(
            `/payment-recon/transactions/${transactionId}/on-account`,
            { method: 'POST', body: JSON.stringify({ note }) }
        );
    },
    deleteAllocation(allocationId: number) {
        return fetchApi<{ transactionId: number; status: string }>(
            `/payment-recon/allocations/${allocationId}`, { method: 'DELETE' }
        );
    },
    unmatch(transactionId: number) {
        return fetchApi<{ transactionId: number; status: string }>(
            `/payment-recon/transactions/${transactionId}/unmatch`, { method: 'POST' }
        );
    },
    aiSuggest(transactionId: number) {
        return fetchApi<{ source: string; suggestion: MatchSuggestion | null }>(
            `/payment-recon/ai-suggest`, { method: 'POST', body: JSON.stringify({ transactionId }) }
        );
    },

    // ---- Imports ------------------------------------------------------------
    listImports(companyCode = DEFAULT_COMPANY) {
        return fetchApi<{ companyCode: string; imports: ReconImport[] }>(
            `/payment-recon/imports${qs({ companyCode })}`
        );
    },
    previewImport(body: { companyCode?: string; templateId?: number | null; mapping?: ColumnMapping; fileBase64: string; fileFormat: string }) {
        return fetchApi<{ headers: string[]; sheets: string[]; sheetName: string; sheetIndex: number | null; sheetResolvedBy: 'index' | 'name' | 'default' | 'none'; totalDataRows: number; creditRows: number; sampleRows: unknown[]; mappingApplied: boolean }>(
            `/payment-recon/imports/preview`, { method: 'POST', body: JSON.stringify({ companyCode: DEFAULT_COMPANY, ...body }) }
        );
    },
    createImport(body: { companyCode?: string; bankAccountId: number; templateId?: number | null; mapping?: ColumnMapping; fileBase64: string; fileName: string; fileFormat: string }) {
        return fetchApi<{ importId: number | null; imported: number; autoMatched: number; needsReview: number; statementFrom: string; statementTo: string; rowsTotal: number; skippedDuplicates: number; overlappingImports: number }>(
            `/payment-recon/imports`, { method: 'POST', body: JSON.stringify({ companyCode: DEFAULT_COMPANY, ...body }) }
        );
    },

    // ---- Lookups / config ---------------------------------------------------
    listCategories() {
        return fetchApi<{ nonCustomer: ReconCategory[]; residual: ReconCategory[] }>(`/payment-recon/categories`);
    },
    listBankAccounts(companyCode = DEFAULT_COMPANY) {
        return fetchApi<{ companyCode: string; bankAccounts: ReconBankAccount[] }>(`/payment-recon/bank-accounts${qs({ companyCode })}`);
    },
    saveBankAccount(body: { id?: number; companyCode?: string; name: string; bankAccountKey: string; iban?: string | null; currency?: string; isActive?: boolean; sortOrder?: number }) {
        return fetchApi<{ id: number }>(`/payment-recon/bank-accounts`, { method: 'POST', body: JSON.stringify({ companyCode: DEFAULT_COMPANY, ...body }) });
    },
    deleteBankAccount(id: number) {
        return fetchApi<{ id: number; isActive: boolean }>(`/payment-recon/bank-accounts/${id}`, { method: 'DELETE' });
    },
    listTemplates(companyCode = DEFAULT_COMPANY) {
        return fetchApi<{ companyCode: string; templates: ReconTemplate[] }>(`/payment-recon/templates${qs({ companyCode })}`);
    },
    saveTemplate(body: { id?: number; companyCode?: string; bankAccountId?: number | null; name: string; fileFormat?: string; mapping: ColumnMapping; isDefault?: boolean }) {
        return fetchApi<{ id: number }>(`/payment-recon/templates`, { method: 'POST', body: JSON.stringify({ companyCode: DEFAULT_COMPANY, ...body }) });
    },
    listAliases(companyCode = DEFAULT_COMPANY) {
        return fetchApi<{ companyCode: string; aliases: ReconAlias[] }>(`/payment-recon/aliases${qs({ companyCode })}`);
    },
    deleteAlias(id: number) {
        return fetchApi<{ id: number }>(`/payment-recon/aliases/${id}`, { method: 'DELETE' });
    },

    // ---- Invoice comments ---------------------------------------------------
    listInvoiceComments(invoiceId: number) {
        return fetchApi<PaymentReconComment[]>(`/payment-recon/invoices/${invoiceId}/comments`);
    },
    createInvoiceComment(invoiceId: number, commentText: string) {
        return fetchApi<PaymentReconComment>(
            `/payment-recon/invoices/${invoiceId}/comments`,
            { method: 'POST', body: JSON.stringify({ commentText }) }
        );
    },
    updateInvoiceComment(invoiceId: number, commentId: number, commentText: string) {
        return fetchApi<PaymentReconComment>(
            `/payment-recon/invoices/${invoiceId}/comments/${commentId}`,
            { method: 'PUT', body: JSON.stringify({ commentText }) }
        );
    },
    async deleteInvoiceComment(invoiceId: number, commentId: number) {
        await fetchApi(`/payment-recon/invoices/${invoiceId}/comments/${commentId}`, { method: 'DELETE' });
    },

    // ---- Stats --------------------------------------------------------------
    getStats(companyCode = DEFAULT_COMPANY) {
        return fetchApi<ReconStats>(`/payment-recon/stats${qs({ companyCode })}`);
    },

    exportUrl(type: 'invoices' | 'transactions', companyCode = DEFAULT_COMPANY) {
        return `/api/v1/payment-recon/export${qs({ type, companyCode })}`;
    },
};
